import { DragEvent, ReactNode, useEffect, useRef, useState } from 'react'

type AttachmentDropzoneProps = {
  accept?: string
  multiple?: boolean
  disabled?: boolean
  onFilesSelected: (files: File[]) => void
  children: (props: { isDragOver: boolean; openFilePicker: () => void }) => ReactNode
}

const AttachmentDropzone = ({
  accept = '',
  multiple = true,
  disabled = false,
  onFilesSelected,
  children,
}: AttachmentDropzoneProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const dragDepthRef = useRef(0)

  useEffect(() => {
    if (disabled) {
      setIsDragOver(false)
      dragDepthRef.current = 0
    }
  }, [disabled])

  const emitFiles = (files: FileList | File[]) => {
    const selectedFiles = Array.from(files as ArrayLike<File>)
    if (!selectedFiles.length) return
    onFilesSelected(selectedFiles)
  }

  const openFilePicker = () => {
    if (disabled) return
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target
    if (target.files && target.files.length > 0) {
      emitFiles(target.files)
      target.value = ''
    }
  }

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return
    event.preventDefault()
    dragDepthRef.current += 1
    setIsDragOver(true)
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return
    event.preventDefault()
    setIsDragOver(true)
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) {
      setIsDragOver(false)
    }
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return
    event.preventDefault()
    dragDepthRef.current = 0
    if (event.dataTransfer?.files?.length) {
      emitFiles(event.dataTransfer.files)
    }
    setIsDragOver(false)
  }

  return (
    <div
      style={{ display: 'contents' }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          border: 0,
        }}
        type="file"
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        onChange={handleFileChange}
      />
      {children({ isDragOver, openFilePicker })}
    </div>
  )
}

export default AttachmentDropzone
